import type {SyntheticEvent} from 'react';
import {buildAvatarData, getAvatarImageUrl} from '@Libraries/user-avatar.library';
import {getReadableAccounts, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';

export interface SocialAccount {
  id: number;
  type: SocialPlatform;
  tikTokMeta: TikTokMeta | null;
  name: string;
  isPreferred: boolean;
  username: string;
  state: AccountState;
  isAccessTokenExpired: boolean;
  isVerifiedTwitterUser: boolean;
  isVerifiedGoogleBusinessProfileAccount: boolean;
  displayURL: string | null;
  isRefreshTokenValid: boolean;
  version: SocialAccountVersion;
  activityType: SocialAccountActivityType;
  metricsStatus: MetricsStatus;
  originalId: string;
  isSharedInTeam: boolean;
  isLockedByPlanLimit: boolean;
  idUser: number;
  healthState: AccountState;
  healthFailureReason: SocialAccountHealthFailureReason | null;
  lastValidatedAt: number | null;
  isReconnectRequired: boolean;
}

export const MAX_POSTERS_PER_PLATFORM: Record<SocialPlatform, number | null> = {
  [SocialPlatform.TWITTER]: 4,
  [SocialPlatform.FACEBOOK_GROUP]: null,
  [SocialPlatform.FACEBOOK_PROFILE]: null,
  [SocialPlatform.FACEBOOK_PAGE]: null,
  [SocialPlatform.FACEBOOK]: null,
  [SocialPlatform.YOUTUBE]: 1,
  [SocialPlatform.LINKEDIN]: 1,
  [SocialPlatform.LINKEDIN_PAGE]: 1,
  [SocialPlatform.LINKEDIN_PROFILE]: 1,
  [SocialPlatform.INSTAGRAM]: 10,
  [SocialPlatform.TIKTOK]: null,
  [SocialPlatform.PINTEREST]: null,
  [SocialPlatform.GOOGLE_BUSINESS_PROFILE]: 1,
  [SocialPlatform.THREADS]: 10,
};

export enum SocialAccountVersion {
  FACEBOOK_LOGIN = 1,
  INSTAGRAM_LOGIN = 2,
}

export enum MetricsStatus {
  UNSUPPORTED = 'UNSUPPORTED',
  PERMISSION_PENDING = 'PERMISSION_PENDING',
  AVAILABLE = 'AVAILABLE',
}

export interface TikTokMeta {
  isCommentDisabled: boolean;
  isStitchDisabled: boolean;
  isDuetDisabled: boolean;
  isPublicAccount: boolean;
  maxVideoDurationSec: number;
}

export interface AccountMediaTypeInformation {
  platformMediaType: PlatformMediaType;
  account: SocialAccount;
}

export enum SocialAccountActivityType {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ABANDONED = 'ABANDONED',
}

/** The account's initial on a colour keyed on the account, for when the platform gave no picture or it no longer loads. */
export const getDefaultDisplayURL = (account: SocialAccount): string => {
  return getAvatarImageUrl(buildAvatarData(String(account.id), account.name, null));
};

export const getDisplayURL = (account: SocialAccount): string => {
  return account.displayURL ?? getDefaultDisplayURL(account);
};

/** An onError handler that swaps a picture that failed to load for the account's initial, once. */
export const swapToDefaultDisplayURL =
  (account: SocialAccount) =>
  (event: SyntheticEvent<HTMLImageElement>): void => {
    const {currentTarget} = event;
    const defaultDisplayURL = getDefaultDisplayURL(account);
    if (currentTarget.src === defaultDisplayURL) {
      return;
    }
    currentTarget.src = defaultDisplayURL;
  };

const isTransientHealthState = (healthState: AccountState): boolean => {
  return healthState === AccountState.NEEDS_VALIDATION || healthState === AccountState.REFRESHING;
};

/** An expired access token is only the user's problem when nothing can refresh it on their behalf. */
const isTokenBeyondSelfRecovery = (account: SocialAccount): boolean => {
  return account.isAccessTokenExpired && !account.isRefreshTokenValid;
};

/**
 * The one derivation of an account's connection status: every reconnect signal is folded in here, so no two
 * surfaces read the connection differently. The plan lock sits on top of it, not in it: see
 * doesUsableAccountNeedReconnection and doesAccountMatchStatusFilter.
 * NEEDS_VALIDATION and REFRESHING are transient internal states only ever cleared server-side, and the
 * frontend never probes, so surfacing them would strand the account on a "Checking…" pill; they resolve to
 * the terminal status the token facts imply instead. The legacy `state` field is never consulted — it reads
 * a refreshable token as EXPIRED, which is what used to make the pill and the banner count disagree.
 */
export const getEffectiveAccountState = (account: SocialAccount): AccountState => {
  if (RECONNECT_REQUIRED_ACCOUNT_STATES.includes(account.healthState)) {
    return account.healthState;
  }

  if (account.isReconnectRequired || isTokenBeyondSelfRecovery(account)) {
    return AccountState.EXPIRED;
  }

  return isTransientHealthState(account.healthState) ? AccountState.CONNECTED : account.healthState;
};

/** Whether the account's connection needs a reconnect, ignoring the plan lock; anything that asks the user to act uses doesUsableAccountNeedReconnection. */
export const doesAccountNeedReconnection = (account: SocialAccount): boolean => {
  return RECONNECT_REQUIRED_ACCOUNT_STATES.includes(getEffectiveAccountState(account));
};

export const isAccountPlanLocked = (account: SocialAccount): boolean => {
  return account.isLockedByPlanLimit;
};

/** Whether to ask the user to reconnect: a plan-locked account stays unusable after a reconnect, and its row offers an upgrade instead. */
export const doesUsableAccountNeedReconnection = (account: SocialAccount): boolean => {
  return !isAccountPlanLocked(account) && doesAccountNeedReconnection(account);
};

export const getDisplayableState = (account: SocialAccount): string => {
  switch (getEffectiveAccountState(account)) {
    case AccountState.NEEDS_REAUTH:
    case AccountState.REVOKED:
    case AccountState.EXPIRED:
      return window.i18next.t('pmwjs_connection_expired');
    case AccountState.HEALTHY:
    case AccountState.CONNECTED:
    default:
      return window.i18next.t('pmwjs_connected');
  }
};

/**
 * The account's own name for display, falling back to the platform label when the name is blank.
 */
export const getAccountDisplayName = (account: SocialAccount): string => {
  return account.name.trim() !== '' ? account.name : getReadableAccounts(account.type);
};

export function isFacebookAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return (
    socialAccount.type === SocialPlatform.FACEBOOK_GROUP ||
    socialAccount.type === SocialPlatform.FACEBOOK_PAGE ||
    socialAccount.type === SocialPlatform.FACEBOOK_PROFILE ||
    socialAccount.type === SocialPlatform.FACEBOOK
  );
}

export function isInstagramAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.INSTAGRAM;
}

export function canInstagramAccountAddReelMusic(socialAccount: SocialAccount): boolean {
  return isInstagramAccount(socialAccount) && socialAccount.version === SocialAccountVersion.FACEBOOK_LOGIN;
}

export function doesAccountSupportMultipleMediaTypes(socialAccount: SocialAccount | undefined): boolean {
  return isFacebookAccount(socialAccount) || isInstagramAccount(socialAccount);
}

export function isTikTokAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.TIKTOK;
}

export function isPinterestAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.PINTEREST;
}

export function isYoutubeAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.YOUTUBE;
}

export function isTwitterAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.TWITTER;
}

export function isLinkedinAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.LINKEDIN_PROFILE || socialAccount.type === SocialPlatform.LINKEDIN || socialAccount.type === SocialPlatform.LINKEDIN_PAGE;
}

export function isGoogleBusinessProfileAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.GOOGLE_BUSINESS_PROFILE;
}

export function isThreadsAccount(socialAccount: SocialAccount | undefined): boolean {
  if (socialAccount === undefined) {
    return false;
  }

  return socialAccount.type === SocialPlatform.THREADS;
}

const updateUniqueMediaTypesArrayIfNeeded = (
  accountMediaTypeInformation: AccountMediaTypeInformation[],
  platformMediaType: PlatformMediaType,
  account: SocialAccount
): AccountMediaTypeInformation[] => {
  const alreadyExists = accountMediaTypeInformation.some((info) => {
    let areFacebookAccounts = false;
    if (isFacebookAccount(info.account) && isFacebookAccount(account)) {
      areFacebookAccounts = true;
    }

    const areBothPostOrReel =
      (info.platformMediaType === PlatformMediaType.POST || info.platformMediaType === PlatformMediaType.REELS) &&
      (platformMediaType === PlatformMediaType.POST || platformMediaType === PlatformMediaType.REELS);

    const areBothStories = info.platformMediaType === PlatformMediaType.STORIES && platformMediaType === PlatformMediaType.STORIES;

    return (info.account.type === account.type || areFacebookAccounts) && (areBothPostOrReel || areBothStories);
  });

  if (!alreadyExists) {
    accountMediaTypeInformation.push({
      account,
      platformMediaType,
    });
  }

  return accountMediaTypeInformation;
};

export const getUniqueAccountMediaTypeCombinations = (socialPost: SocialPost): AccountMediaTypeInformation[] => {
  let accountMediaTypeInformation: AccountMediaTypeInformation[] = [];
  const {accounts} = socialPost;

  for (let i = 0; i < socialPost.accounts.length; i++) {
    const allPublishingParamsForAccount = socialPost.publishingParams[accounts[i].id];

    if (!allPublishingParamsForAccount) {
      continue;
    }

    if (allPublishingParamsForAccount[PlatformMediaType.POST] || allPublishingParamsForAccount[PlatformMediaType.REELS]) {
      const isPost = allPublishingParamsForAccount[PlatformMediaType.POST] !== null;
      const mediaType = isPost ? PlatformMediaType.POST : PlatformMediaType.REELS;
      accountMediaTypeInformation = updateUniqueMediaTypesArrayIfNeeded(accountMediaTypeInformation, mediaType, accounts[i]);
    }

    if (allPublishingParamsForAccount[PlatformMediaType.STORIES]) {
      accountMediaTypeInformation = updateUniqueMediaTypesArrayIfNeeded(accountMediaTypeInformation, PlatformMediaType.STORIES, accounts[i]);
    }
  }

  return accountMediaTypeInformation;
};

export enum AccountState {
  EXPIRED = 'EXPIRED',
  CONNECTED = 'CONNECTED',
  HEALTHY = 'HEALTHY',
  NEEDS_VALIDATION = 'NEEDS_VALIDATION',
  REFRESHING = 'REFRESHING',
  NEEDS_REAUTH = 'NEEDS_REAUTH',
  REVOKED = 'REVOKED',
}

/** The terminal account states that mean "the user must reconnect". Single source of truth for the reconnect filter and the NEEDS_RECONNECTION checkbox group. */
export const RECONNECT_REQUIRED_ACCOUNT_STATES: AccountState[] = [AccountState.EXPIRED, AccountState.NEEDS_REAUTH, AccountState.REVOKED];

export enum SocialAccountHealthFailureReason {
  TOKEN_EXPIRED_REFRESH_FAILED = 'TOKEN_EXPIRED_REFRESH_FAILED',
  TOKEN_REVOKED_BY_USER = 'TOKEN_REVOKED_BY_USER',
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED',
  INSUFFICIENT_SCOPES = 'INSUFFICIENT_SCOPES',
  APP_DEPRECATED = 'APP_DEPRECATED',
  PLATFORM_RATE_LIMITED = 'PLATFORM_RATE_LIMITED',
  UNKNOWN_API_ERROR = 'UNKNOWN_API_ERROR',
}
